/**
 * antd / rc-field-form rule evaluation, covering the rule shapes this codebase uses:
 * required, type, min, max, len, pattern, enum, whitespace, validator, and the
 * function form `(form) => rule`.
 */

const isEmpty = (value) => {
  if (value == null) return true
  if (typeof value === 'string') return value.length === 0
  if (Array.isArray(value)) return value.length === 0
  return false
}

const TYPE_CHECKS = {
  string: (value) => typeof value === 'string',
  number: (value) => typeof value === 'number' && !Number.isNaN(value),
  boolean: (value) => typeof value === 'boolean',
  array: (value) => Array.isArray(value),
  object: (value) => value !== null && typeof value === 'object' && !Array.isArray(value),
  email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)),
  url: (value) => /^https?:\/\/\S+$/.test(String(value)),
  integer: (value) => Number.isInteger(Number(value)),
  date: (value) => value instanceof Date || (value?.isValid?.() ?? false),
}

const defaultMessage = (rule, label) => {
  const field = label || 'Ce champ'
  if (rule.required) return `${field} est obligatoire`
  if (rule.type && TYPE_CHECKS[rule.type]) return `${field} n'est pas valide`
  if (rule.pattern) return `${field} n'est pas au bon format`
  if (rule.len != null) return `${field} doit contenir ${rule.len} caractères`
  if (rule.min != null) return `${field} est trop petit`
  if (rule.max != null) return `${field} est trop grand`
  if (rule.enum) return `${field} n'est pas une valeur autorisée`
  return `${field} n'est pas valide`
}

/** Length for strings/arrays, magnitude for numbers, matching antd. */
const measure = (value) => {
  if (typeof value === 'number') return value
  if (typeof value === 'string' || Array.isArray(value)) return value.length
  return null
}

const checkRule = (value, rule, label) => {
  const message = rule.message ?? defaultMessage(rule, label)

  if (rule.required && isEmpty(value)) return message
  if (rule.whitespace && typeof value === 'string' && value.trim().length === 0) return message

  // Every other rule is skipped on an empty value, as antd does.
  if (isEmpty(value)) return null

  if (rule.type && TYPE_CHECKS[rule.type] && !TYPE_CHECKS[rule.type](value)) return message

  const size = measure(value)
  if (rule.len != null && size !== rule.len) return message
  if (rule.min != null && size != null && size < rule.min) return message
  if (rule.max != null && size != null && size > rule.max) return message

  if (rule.pattern && !new RegExp(rule.pattern).test(String(value))) return message
  if (rule.enum && !rule.enum.includes(value)) return message

  return null
}

/** Runs a `validator` rule, which may throw, reject, or call `callback(error)`. */
const runValidator = async (rule, value, label) => {
  try {
    const viaCallback = []
    const result = rule.validator(rule, value, (error) => {
      if (error) viaCallback.push(String(error?.message || error))
    })
    if (result && typeof result.then === 'function') await result
    return viaCallback
  } catch (error) {
    return [String(error?.message || rule.message || defaultMessage(rule, label))]
  }
}

/**
 * Runs a field's rules and resolves with the error messages.
 *
 * Asynchronous because `validator` rules in this codebase commonly return a
 * promise (uniqueness checks against the API, cross-field date checks).
 *
 * @param {any} value
 * @param {Array} rules
 * @param {{label?: string, getFieldValue?: Function}} context
 * @returns {Promise<string[]>}
 */
export const validateValue = async (value, rules, context = {}) => {
  if (!Array.isArray(rules) || rules.length === 0) return []
  const { label, getFieldValue } = context
  const messages = []

  for (const rawRule of rules) {
    if (!rawRule) continue
    // antd allows `rules={[({ getFieldValue }) => ({ validator })]}`
    const rule = typeof rawRule === 'function' ? rawRule({ getFieldValue }) : rawRule
    if (!rule) continue

    if (typeof rule.validator === 'function') {
      // eslint-disable-next-line no-await-in-loop -- rules run in order, as antd does
      messages.push(...(await runValidator(rule, value, label)))
      continue
    }

    const failure = checkRule(value, rule, label)
    if (failure) messages.push(String(failure))
  }

  return messages
}

export { checkRule, isEmpty, TYPE_CHECKS }
